import React, { useEffect, useState } from "react";

import _ from "lodash";
import { Moment } from "moment";
import { useTranslation } from "react-i18next";

import { Button, DatePicker, Form, Input, Row, Spin } from "antd";

export interface BookingsFormErrors {
  dateFrom?: string;
  dateTo?: string;
  url?: string;
}

export interface BookingsFormProps {
  className?: string;

  dateFrom?: Moment;
  dateTo?: Moment;
  partner?: string;
  password?: string;
  url?: string;
  errors?: BookingsFormErrors;

  onFinish?: (value: BookingsFormResult) => void;
  isLoading?: boolean;
}

export interface BookingsFormResult {
  dateFrom: Moment;
  dateTo: Moment;
  partner?: string;
  password?: string;
  url: string;
}

export const BookingsForm: React.FC<BookingsFormProps> = (props) => {
  const { t } = useTranslation("gate_tester_page");

  const [state, setState] = useState({ dateFrom: props.dateFrom, errors: props.errors ?? {} });

  useEffect(() => {
    setState((c) => ({ ...c, errors: props.errors ?? {} }));
  }, [props.errors]);

  const handleValuesChange = (values: {}) => {
    setState((currentState) => {
      if (_.isEmpty(currentState.errors)) {
        return currentState;
      }
      const errors = _.keys(currentState.errors).reduce((current, field) => {
        if (field in values) {
          return current;
        }
        return { ...current, [field]: currentState.errors[field as keyof BookingsFormErrors] };
      }, {});
      return { ...currentState, errors };
    });
  };

  const handleChangeDateFrom = (value: Moment | null, dateStr: string) => {
    setState((currentState) => ({ ...currentState, dateFrom: value ?? undefined }));
  };
  const disabledDateTo = (current: Moment): boolean => {
    return state.dateFrom ? current.isBefore(state.dateFrom) : false;
  };

  return (
    <Spin spinning={props.isLoading ?? false}>
      <Form
        name="bookings-statistic"
        layout="vertical"
        onFinish={props.onFinish}
        className={props.className}
        onValuesChange={handleValuesChange}
      >
        <Row>
          <Form.Item
            label={t("forms.bookings.dateFrom.label")}
            name={"dateFrom"}
            validateTrigger={["onFocus", "onChange"]}
            rules={[{ required: true, message: t("forms.bookings.dateFrom.message") }]}
            initialValue={props.dateFrom}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.dateFrom ? "error" : undefined}
            help={state.errors.dateFrom}
          >
            <DatePicker style={{ width: "100%" }} onChange={handleChangeDateFrom} />
          </Form.Item>
          <Form.Item
            label={t("forms.bookings.dateTo.label")}
            name={"dateTo"}
            validateTrigger={["onFocus", "onChange"]}
            rules={[{ required: true, message: t("forms.bookings.dateTo.message") }]}
            initialValue={props.dateTo}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.dateTo ? "error" : undefined}
            help={state.errors.dateTo}
          >
            <DatePicker style={{ width: "100%" }} disabledDate={disabledDateTo} />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item label={t("forms.bookings.credential.partner.label")} name="partner" initialValue={props.partner}>
            <Input placeholder={t("forms.bookings.credential.partner.placeholder")} autoComplete={"off"} />
          </Form.Item>
          <Form.Item
            label={t("forms.bookings.credential.password.label")}
            name="password"
            initialValue={props.password}
          >
            <Input.Password placeholder={t("forms.bookings.credential.password.placeholder")} autoComplete={"off"} />
          </Form.Item>
        </Row>
        <Form.Item
          label={t("forms.bookings.url.label")}
          name="url"
          validateTrigger={["onFocus", "onChange"]}
          rules={[
            { required: true, message: t("forms.bookings.url.messages.required") },
            { type: "url", message: t("forms.bookings.url.messages.invalid") },
          ]}
          initialValue={props.url}
          validateStatus={state.errors.url ? "error" : undefined}
          help={state.errors.url}
        >
          <Input placeholder={t("forms.bookings.url.placeholder")} autoComplete={"off"} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t("forms.bookings.submit")}
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};
